import {
  clsx,
  type ClassValue,
} from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * tailwind-merge needs to know about our custom CadActive theme tokens
 * so it can de-duplicate correctly. Without this config it would, for
 * example, treat `text-t1` (font-size) and `text-gray-5` (text color)
 * as conflicting because they both start with `text-`.
 *
 * See src/assets/styles/tailwind.css for the canonical token list.
 */
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      // Indexed spacing scale (mirrors src/assets/themes/base/space.ts).
      // tailwind-merge uses `spacing` for margin/padding/gap/etc.
      spacing: [
        't0', 't1', 't2', 't3', 't4', 't5',
        't6', 't7', 't8', 't9', 't10', 't11',
      ],
      // Border radius indexed scale.
      radius: ['t0', 't1', 't2', 't3'],
      // Box shadow indexed scale.
      shadow: ['t0', 't1', 't2', 't3', 't4', 't5'],
    },
    classGroups: {
      // Indexed font-size scale (mirrors theme.fontSizes).
      // Declared separately so `text-t1` is recognised as a font-size,
      // not a text color, and so it does NOT collide with `text-gray-5`,
      // `text-primary`, `text-foreground`, etc.
      'font-size': [
        { text: ['t0', 't1', 't2', 't3', 't4', 't5', 't6', 't7', 't8', 't9', 't10', 't11'] },
      ],
      // CadActive semantic text-color utilities. Listed so tailwind-merge
      // knows these are text colors (conflicts with other text-color
      // utilities like text-gray-5) rather than font-size utilities.
      'text-color': [
        {
          text: [
            'background', 'surface', 'muted',
            'foreground', 'foreground-emphasis', 'muted-foreground',
            'border', 'border-emphasis', 'ring',
            'primary', 'primary-hover', 'primary-foreground', 'primary-muted',
            'destructive', 'destructive-hover', 'destructive-foreground', 'destructive-muted',
            'success', 'success-hover', 'success-foreground', 'success-muted',
            'warning', 'warning-hover', 'warning-foreground', 'warning-muted',
          ],
        },
      ],
    },
  },
});

/**
 * Compose Tailwind CSS class names into a single string.
 *
 * Wraps two conventions that the rest of the app depends on:
 *
 *  1. `clsx` — handles conditional class names, arrays, objects, and
 *     falsy values. Lets callers write things like
 *     `cn('base', isPrimary && 'bg-primary', { 'opacity-50': disabled })`.
 *
 *  2. `tailwind-merge` — deduplicates conflicting Tailwind utilities so
 *     the last one wins. Without this, `cn('p-2', 'p-4')` would produce
 *     `'p-2 p-4'` (rendering `p-4` in the DOM but keeping the shorter
 *     dead class), which is a common source of override bugs when a
 *     component spreads a caller-provided `className` on top of its own
 *     defaults.
 *
 * This is the single blessed entry point for building `className` strings
 * across the codebase. Prefer it over raw `clsx`, template literals, or
 * inline ternaries. See docs/tailwind-migration.md for usage patterns.
 *
 * @example
 *   cn('rounded-md border border-border', className)
 *
 * @example
 *   cn(
 *     'inline-flex items-center gap-2',
 *     variant === 'primary' && 'bg-primary text-primary-foreground',
 *     variant === 'destructive' && 'bg-destructive text-destructive-foreground',
 *     disabled && 'opacity-50 cursor-not-allowed',
 *     className,
 *   )
 */
export const cn = (...inputs: ClassValue[]): string => twMerge(clsx(inputs));
